// src/services/scheduledPostsApi.js
const API_URL = process.env.REACT_APP_API_URL;

/**
 * Create a scheduled post
 * @param {Object} postData - {content: string, scheduled_at: Date}
 * @returns {Promise<Object>} - Scheduled post response
 */
export const createScheduledPost = async (postData) => {
    const response = await fetch(`${API_URL}/api/scheduled-posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
            content: postData.content,
            scheduled_at: postData.scheduled_at.toISOString(),
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to schedule post");
    }

    return response.json();
};

/**
 * Get list of scheduled posts
 * @param {Object} filters - {status?: string, limit?: number, skip?: number}
 * @returns {Promise<Array>} - List of scheduled posts
 */
export const getScheduledPosts = async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append("status", filters.status);
    if (filters.limit) params.append("limit", filters.limit);
    if (filters.skip) params.append("skip", filters.skip);

    const response = await fetch(
        `${API_URL}/api/scheduled-posts?${params.toString()}`,
        {
            method: "GET",
            credentials: "include",
        }
    );

    if (!response.ok) {
        throw new Error("Failed to fetch scheduled posts");
    }

    return response.json();
};

/**
 * Get a single scheduled post
 * @param {string} postId - Post ID
 * @returns {Promise<Object>} - Scheduled post details
 */
export const getScheduledPost = async (postId) => {
    const response = await fetch(`${API_URL}/api/scheduled-posts/${postId}`, {
        method: "GET",
        credentials: "include",
    });

    if (!response.ok) {
        throw new Error("Failed to fetch post");
    }

    return response.json();
};

/**
 * Update a scheduled post
 * @param {string} postId - Post ID
 * @param {Object} updates - {content?: string, scheduled_at?: Date}
 * @returns {Promise<Object>} - Updated post
 */
export const updateScheduledPost = async (postId, updates) => {
    const body = {};
    if (updates.content) body.content = updates.content;
    if (updates.scheduled_at) body.scheduled_at = updates.scheduled_at.toISOString();

    const response = await fetch(`${API_URL}/api/scheduled-posts/${postId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to update post");
    }

    return response.json();
};

/**
 * Delete a scheduled post
 * @param {string} postId - Post ID
 * @returns {Promise<Object>} - Deletion confirmation
 */
export const deleteScheduledPost = async (postId) => {
    const response = await fetch(`${API_URL}/api/scheduled-posts/${postId}`, {
        method: "DELETE",
        credentials: "include",
    });

    if (!response.ok) {
        throw new Error("Failed to delete post");
    }

    return response.json();
};

/**
 * Publish a scheduled post immediately
 * @param {string} postId - Post ID
 * @returns {Promise<Object>} - Publish confirmation
 */
export const publishNow = async (postId) => {
    const response = await fetch(
        `${API_URL}/api/scheduled-posts/${postId}/publish-now`,
        {
            method: "POST",
            credentials: "include",
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to publish post");
    }

    return response.json();
};
